// Only the pure content-type resolver — this module is reached from the wizard and must not
// drag an auth stack in behind it.
import { resolveContentType } from '@/engine/runtime/contentType';

/**
 * Content the creator brings to a build — and an honest answer about what will happen to it.
 *
 * The failure this exists to prevent: accepting somebody's PDF, building an app that silently
 * ignores it, and letting them discover that only by reading the result. A file we cannot use
 * is refused HERE, with a sentence naming what to do instead, rather than being carried
 * through a build that will not mention it.
 *
 * Three outcomes, deliberately, because they have three different destinations:
 *  - `document` — text the app is built AROUND. Goes to POST /apps/{id}/text, is split into
 *    parts, and is read at runtime through `S.asset`. Never enters the prompt.
 *  - `media`    — a picture, clip or sound the app SHOWS. Goes through the presigned upload
 *    once the app exists, and its URL is given to the generator.
 *  - `unsupported` — everything else, named and explained.
 */

export type PickedContent =
  | { kind: 'document'; fileName: string; title: string; text: string }
  | { kind: 'media'; fileName: string; uri: string; contentType: string }
  | { kind: 'unsupported'; fileName: string; reason: string };

/** Extensions we can read as plain text. Deliberately short: a format we cannot read as text
 *  must be refused, not guessed at. */
const TEXT_EXTENSIONS = ['txt', 'text', 'md', 'markdown', 'csv', 'tsv'];

/**
 * The document ceiling.
 *
 * The text travels as a JSON body to API Gateway (10 MB) and on to Lambda (6 MB), and UTF-8
 * Arabic or CJK is 2-3 bytes per character — so the character count is not the bound, the
 * encoded size is. 4 MB leaves real headroom under the smaller of the two limits. This has
 * NOT been measured against a real 6,236-verse file; it is a deliberately conservative guess
 * and the message says so rather than pretending to precision.
 */
export const MAX_DOCUMENT_BYTES = 4_000_000;

function extensionOf(fileName: string): string {
  return (fileName || '').toLowerCase().split('.').pop() ?? '';
}

/** Turn a title out of a filename: "the-quran (1).txt" → "The Quran". */
export function titleFromFileName(fileName: string): string {
  const base = (fileName || '').replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').replace(/\s*\(\d+\)\s*$/, '').trim();
  if (!base) return 'Untitled';
  return base.charAt(0).toUpperCase() + base.slice(1);
}

/**
 * What a file IS, decided without reading it.
 *
 * Pure and exported so it can be tested without a picker: this is the decision that governs
 * whether somebody's upload is used or ignored, and it should not be the untested part.
 */
export function classifyPick(
  fileName: string,
  mimeType?: string | null,
): { kind: 'document' } | { kind: 'media'; contentType: string } | { kind: 'unsupported'; reason: string } {
  const ext = extensionOf(fileName);
  const mime = (mimeType ?? '').trim().toLowerCase();

  // Text first. `resolveContentType` would refuse it — its allowlist is image/audio/video —
  // and that refusal is correct for the UPLOAD path but wrong here, because text has its own
  // destination that does not go through the upload allowlist at all.
  if (TEXT_EXTENSIONS.includes(ext) || mime.startsWith('text/')) return { kind: 'document' };

  // Then media, judged by exactly the same function the uploader will use, so a file accepted
  // here cannot be rejected a step later.
  const contentType = resolveContentType(mimeType, fileName);
  if (contentType) return { kind: 'media', contentType };

  if (ext === 'pdf' || mime === 'application/pdf') {
    return {
      kind: 'unsupported',
      reason: 'PDFs cannot be read yet. Save it as a .txt and I will build the app around it.',
    };
  }
  if (['doc', 'docx', 'pages', 'rtf', 'odt'].includes(ext)) {
    return {
      kind: 'unsupported',
      reason: `A ${ext.toUpperCase()} cannot be read yet. Export it as a .txt and I will use it.`,
    };
  }
  if (['epub', 'mobi', 'azw3'].includes(ext)) {
    return { kind: 'unsupported', reason: 'E-books cannot be read yet. A .txt of the text works.' };
  }
  return {
    kind: 'unsupported',
    reason: 'That kind of file cannot be used yet — text, photos, audio and video can.',
  };
}

/**
 * Whether a document is complete enough to build with.
 *
 * Lives here, as a pure function, because this repo has no component tests — so a rule left
 * inside the wizard screen would be the untested part, and this one decides whether somebody's
 * document is used at all. The server refuses a text with no attribution, and inventing one
 * would defeat the reason it is required: a translation is somebody's work and is shown to
 * readers. So an incomplete document is not half-attached, it is not attached.
 */
export function documentReady(
  doc: { title: string; attribution: string; text: string } | null,
): boolean {
  if (!doc) return false;
  return doc.text.trim().length > 0
    && doc.title.trim().length > 0
    && doc.attribution.trim().length > 0;
}

/** Why a document was refused after being read, or null when it is fine. */
export function documentProblem(text: string, byteLength: number): string | null {
  if (!text.trim()) return 'That file has no text in it.';
  if (byteLength > MAX_DOCUMENT_BYTES) {
    const mb = (byteLength / 1_000_000).toFixed(1);
    return `That document is ${mb} MB, which is more than can be sent in one go. Split it and attach one part.`;
  }
  return null;
}

/** Read a Blob as text through a FileReader, which is the part React Native actually has. */
function blobToText(blob: Blob, makeReader: () => FileReader): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = makeReader();
    reader.onerror = () => reject(new Error('could not read that file'));
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.readAsText(blob);
  });
}

/**
 * The contents of a picked file, read from its local uri.
 *
 * The picker hands back a `uri`, never the text: its `base64` field is **web only**, so on a
 * phone — the only place this runs — it is always undefined.
 *
 * BLOB FIRST, deliberately. `fetch(uri).text()` on a `file://` uri has no precedent anywhere
 * in this repo; `fetch(uri).blob()` does (`uploadAsset.ts`), and it is the path every upload
 * already depends on. `FileReader.readAsText` then turns that into text, and React Native
 * provides both. `res.text()` remains only for an environment with no FileReader.
 *
 * The choice is made BEFORE the body is touched, because a response body can only be consumed
 * once — trying blob and then falling back to text on the same response throws.
 */
export async function readTextFromUri(
  uri: string,
  deps?: { fetchImpl?: typeof fetch; readerFactory?: () => FileReader },
): Promise<string> {
  const doFetch = deps?.fetchImpl ?? fetch;
  const makeReader = deps?.readerFactory
    ?? (typeof FileReader !== 'undefined' ? () => new FileReader() : undefined);
  const res = await doFetch(uri);
  if (!makeReader) return res.text();
  return blobToText(await res.blob(), makeReader);
}

/**
 * Pick one file and say what it is.
 *
 * Returns null when the person cancelled — which is not a failure and must not be reported as
 * one. Anything that genuinely went wrong comes back as `unsupported` with a reason, never as
 * a fabricated value: the picker component in the engine answers a failed pick with the
 * literal string 'document.pdf', which is the shape of bug this file exists to avoid.
 */
export async function pickContent(
  deps?: { fetchImpl?: typeof fetch; readerFactory?: () => FileReader },
): Promise<PickedContent | null> {
  let picked: { name: string; uri: string; mimeType?: string | null; size?: number } | undefined;
  try {
    const { getDocumentAsync } = await import('expo-document-picker');
    const result = await getDocumentAsync({ copyToCacheDirectory: true });
    if (result.canceled) return null;
    picked = result.assets?.[0];
  } catch {
    return { kind: 'unsupported', fileName: '', reason: 'The file picker could not open just now.' };
  }
  if (!picked) return null;

  const fileName = picked.name || 'file';
  const verdict = classifyPick(fileName, picked.mimeType);

  if (verdict.kind === 'unsupported') return { kind: 'unsupported', fileName, reason: verdict.reason };
  if (verdict.kind === 'media') {
    return { kind: 'media', fileName, uri: picked.uri, contentType: verdict.contentType };
  }

  let text: string;
  try {
    text = await readTextFromUri(picked.uri, deps);
  } catch {
    return { kind: 'unsupported', fileName, reason: 'That file could not be read. Try attaching it again.' };
  }

  const problem = documentProblem(text, picked.size ?? text.length);
  if (problem) return { kind: 'unsupported', fileName, reason: problem };

  return { kind: 'document', fileName, title: titleFromFileName(fileName), text };
}
